import { useState } from "react";
import {
	Box,
	Button,
	ButtonGroup,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { Close, Download } from "@mui/icons-material";
import type { MissionCSV } from "@rogoag/shared-ts";

export interface CsvDryRunDialogProps {
	open: boolean;
	onClose: () => void;
	csvs: MissionCSV[];
	onDownload: () => void;
}

export function CsvDryRunDialog({ open, onClose, csvs, onDownload }: CsvDryRunDialogProps) {
	const [selectedIndex, setSelectedIndex] = useState(0);
	const csv = csvs[selectedIndex];
	const lines =
		csv
			?.toString()
			.split("\n")
			.filter((l) => l.length > 0) ?? [];
	const headers = lines[0]?.split(",") ?? [];
	const rows = lines.slice(1).map((line) => line.split(","));

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper" disableScrollLock>
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
					CSV Dry-Run
					{csvs.length > 1 && (
						<ButtonGroup size="small">
							{csvs.map((c, i) => (
								<Button
									key={c.filename}
									variant={i === selectedIndex ? "contained" : "outlined"}
									onClick={() => setSelectedIndex(i)}
								>
									{c.filename}
								</Button>
							))}
						</ButtonGroup>
					)}
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<IconButton onClick={onDownload} size="small">
						<Download />
					</IconButton>
					<IconButton onClick={onClose} size="small">
						<Close />
					</IconButton>
				</Box>
			</DialogTitle>
			<DialogContent dividers>
				{csv && (
					<>
						<Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
							{csv.filename} — {rows.length} rows
						</Typography>
						<TableContainer>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										{headers.map((h, i) => (
											<TableCell key={i} sx={{ whiteSpace: "nowrap" }}>
												{h}
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{rows.map((row, ri) => (
										<TableRow key={ri}>
											{headers.map((_, ci) => (
												<TableCell key={ci} sx={{ whiteSpace: "nowrap" }}>
													{row[ci] || ""}
												</TableCell>
											))}
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}
